import React, {useCallback, useState} from 'react';
import AuthActions from 'actions/AuthActions';
import LoginInputLabel from '@components/input-label/LoginInputLabel';
import LoginTextField from '@components/text-field/LoginTextField';
import Button from '@material-ui/core/Button';
import LoginErrorContainer from 'components/LoginErrorContainer';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import {LOGIN_REDIRECT_PARAM} from '@components/login/LoginConstants';
import {LoginTypeResponse} from 'stores/AuthSource';
import {makeStyles} from '@material-ui/core/styles';
import {Link} from 'react-router';

interface PasswordLoginStepProps {
  username: string;
  setLoginTypeResponse: React.Dispatch<React.SetStateAction<LoginTypeResponse | null>>;
}

export const PasswordLoginStep: React.FC<PasswordLoginStepProps> = props => {
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [captcha, setCaptcha] = useState<any>(); // Managed by LoginErrorContainer
  const [submitted, setSubmitted] = useState(false);

  const searchParams = new URLSearchParams(window.location.search);
  const redirectPath = searchParams.get(LOGIN_REDIRECT_PARAM);

  const submit = useCallback(
    e => {
      e.preventDefault();
      setSubmitted(true);
      if (redirectPath) {
        AuthActions.saveNext(redirectPath);
      }
      return AuthActions.login({
        email: props.username,
        password,
        remember: rememberMe,
        captchaResponse: captcha,
      }).finally(() => setSubmitted(false));
    },
    [captcha, props, password, rememberMe, redirectPath]
  );

  const css = useStyles();

  return (
    <div className={css.parent}>
      <h3 className={css.header}>Enter your password</h3>
      <form onSubmit={e => submit(e)}>
        <LoginInputLabel htmlFor="login-username" className={css.emailLabel}>
          Email
        </LoginInputLabel>
        <LoginTextField
          name="username"
          id="login-username"
          disabled={true}
          className={css.emailField}
          value={props.username}
        />
        <LoginInputLabel htmlFor="login-password" className={css.passwordLabel}>
          Password
        </LoginInputLabel>
        <LoginTextField
          name="password"
          id="login-password"
          type="password"
          className={css.passwordField}
          onChange={e => setPassword(e.target.value)}
          autoFocus={true}
        />
        <Link className={css.forgotPassword} to="/password/forgot">
          Forgot password
        </Link>
        <LoginErrorContainer setCaptcha={setCaptcha} />
        <div className={css.controls}>
          <FormControlLabel
            className={css.rememberMe}
            control={
              <Checkbox onChange={e => setRememberMe(e.target.checked)} checked={rememberMe} />
            }
            value={rememberMe}
            label={<span>Remember Me</span>}
          />
          <Button onClick={() => props.setLoginTypeResponse(null)} className={css.button}>
            Back
          </Button>
          <Button
            size="large"
            color="primary"
            type="submit"
            variant="contained"
            className={css.button}
            disabled={submitted}>
            Login
          </Button>
        </div>
      </form>
    </div>
  );
};

const useStyles = makeStyles(() => {
  return {
    parent: {
      margin: 'auto',
      width: '75%',
    },
    header: {
      marginBottom: '1rem',
      fontWeight: 500,
    },
    emailLabel: {
      fontWeight: 'normal',
    },
    emailField: {
      width: '100%',
      marginBottom: '1rem',
    },
    passwordLabel: {
      fontWeight: 'normal',
    },
    passwordField: {
      width: '100%',
      marginBottom: '1rem',
    },
    forgotPassword: {
      color: 'grey',
      marginBottom: '1rem',
    },
    button: {
      margin: '1rem 0',
      fontWeight: 'normal',
    },
    controls: {
      display: 'flex',
      justifyContent: 'right',
    },
    rememberMe: {
      marginRight: 'auto',
    },
  };
});
